<script setup>
import { ref, computed, nextTick, onMounted } from 'vue'

import LayoutShell from '@/components/layout/layoutShell.vue'
import LayoutFooter from '@/components/layout/layoutFooter.vue'
import DashSection from '@/components/dashboard/DashSection.vue'
import WidgetCatalogDrawer from '@/components/dashboard/WidgetCatalogDrawer.vue'

import { useDashboardStore } from '@/stores/dashboard'
import { useI18n } from '@/i18n'

const dashStore = useDashboardStore()
const { t } = useI18n()

// ── Widget-Katalog (Drawer von rechts) ────────────────────────────────────
const showCatalog     = ref(false)
const catalogSection  = ref(null)   // Ziel-Section-Id

function openCatalog(sectionId = null) {
  catalogSection.value =
    sectionId ?? dashStore.sections[dashStore.sections.length - 1]?.id ?? null
  if (!catalogSection.value) {
    // Kein Abschnitt vorhanden → erst einen anlegen
    dashStore.addSection()
    catalogSection.value = dashStore.sections[dashStore.sections.length - 1]?.id
  }
  showCatalog.value = true
}

// ── Rename state (inline) ─────────────────────────────────────────────────
const renamingId    = ref(null)
const renameValue   = ref('')
const renameInputEl = ref(null)

function startRename(dash) {
  renamingId.value  = dash.id
  renameValue.value = dash.name
  nextTick(() => renameInputEl.value?.[0]?.focus())
}

async function confirmRename() {
  if (!renamingId.value) return
  await dashStore.renameDashboard(renamingId.value, renameValue.value)
  renamingId.value = null
}

function cancelRename() {
  renamingId.value = null
}

// ── Save & exit ────────────────────────────────────────────────────────────
async function saveAndExit() {
  await dashStore.saveActiveLayout()
  dashStore.exitEditMode()
}

const isEmpty = computed(() =>
  dashStore.sections.length === 0 ||
  dashStore.sections.every(s => s.layout.length === 0)
)

onMounted(() => dashStore.fetchDashboards())
</script>

<template>
  <LayoutShell>
    <main class="dash-main">

      <!-- ── Header: tab strip + actions ─────────────────────────────── -->
      <div class="dash-header">

        <!-- Tab strip -->
        <div class="dash-tabs">
          <div
            v-for="dash in dashStore.dashboards"
            :key="dash.id"
            class="dash-tab"
            :class="{ 'is-active': dash.id === dashStore.activeDashId }"
            @click="dashStore.switchDashboard(dash.id)"
          >
            <!-- Inline rename input -->
            <template v-if="dashStore.editMode && renamingId === dash.id">
              <input
                ref="renameInputEl"
                v-model="renameValue"
                class="tab-rename-input"
                maxlength="100"
                @blur="confirmRename"
                @keydown.enter.prevent="confirmRename"
                @keydown.escape.prevent="cancelRename"
                @click.stop
              />
            </template>

            <!-- Normal tab label -->
            <template v-else>
              <span class="tab-name">{{ dash.name }}</span>
              <span v-if="dashStore.editMode" class="tab-actions">
                <button
                  class="tab-btn"
                  :title="t('dashboard.rename')"
                  @click.stop="startRename(dash)"
                >
                  <i class="pi pi-pencil" />
                </button>
                <button
                  v-if="dashStore.dashboards.length > 1"
                  class="tab-btn tab-btn--delete"
                  :title="t('dashboard.deleteDashboard')"
                  @click.stop="dashStore.deleteDashboard(dash.id)"
                >
                  <i class="pi pi-trash" />
                </button>
              </span>
            </template>
          </div>

          <!-- New dashboard button -->
          <button
            class="dash-tab-new"
            :title="t('dashboard.newDashboard')"
            @click="dashStore.createDashboard()"
          >
            <i class="pi pi-plus" />
          </button>
        </div>

        <!-- Right-side actions -->
        <div class="dash-actions">
          <span v-if="dashStore.saving" class="dash-saving">
            <i class="pi pi-spin pi-spinner" /> {{ t('dashboard.saving') }}
          </span>
          <template v-if="dashStore.editMode">
            <pButton
              icon="pi pi-plus"
              :label="t('dashboard.addWidget')"
              size="small"
              outlined
              @click="openCatalog()"
            />
            <pButton
              icon="pi pi-clone"
              :label="t('dashboard.addSection')"
              size="small"
              severity="secondary"
              outlined
              @click="dashStore.addSection()"
            />
            <pButton
              icon="pi pi-refresh"
              :label="t('dashboard.reset')"
              size="small"
              severity="secondary"
              outlined
              @click="dashStore.resetToDefault()"
            />
            <pButton
              icon="pi pi-check"
              :label="t('common.done')"
              size="small"
              :loading="dashStore.saving"
              @click="saveAndExit"
            />
          </template>
          <pButton
            v-else
            icon="pi pi-pencil"
            :label="t('common.edit')"
            size="small"
            outlined
            @click="dashStore.toggleEditMode()"
          />
        </div>
      </div>

      <!-- ── Content ────────────────────────────────────────────────── -->
      <div v-if="dashStore.loading" class="dash-loading">
        <i class="pi pi-spin pi-spinner" style="font-size: 1.5rem" />
      </div>

      <div v-else-if="isEmpty && !dashStore.editMode" class="dash-empty">
        <i class="pi pi-th-large" />
        <p>{{ t('dashboard.empty') }}</p>
        <pButton
          icon="pi pi-plus"
          :label="t('dashboard.addWidget')"
          size="small"
          outlined
          @click="dashStore.toggleEditMode(); openCatalog()"
        />
      </div>

      <div v-else class="dash-sections">
        <DashSection
          v-for="(section, idx) in dashStore.sections"
          :key="section.id"
          :section="section"
          :index="idx"
          :total="dashStore.sections.length"
          @add-widget="openCatalog"
        />
      </div>

      <div class="layout-footer">
        <LayoutFooter />
      </div>
    </main>

    <!-- ── Widget catalog drawer (von rechts, volle Höhe) ───────────── -->
    <WidgetCatalogDrawer
      v-model:visible="showCatalog"
      :section-id="catalogSection"
    />
  </LayoutShell>
</template>

<style scoped>
.dash-main {
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* ── Header ─────────────────────────────────────────────────────────────── */
.dash-header {
  display: flex;
  align-items: stretch;
  gap: 0;
  border-bottom: 1px solid var(--p-content-border-color, #e2e8f0);
  background: var(--p-content-background, #fff);
  flex-shrink: 0;
  min-height: 42px;
}

/* ── Tab strip ──────────────────────────────────────────────────────────── */
.dash-tabs {
  display: flex;
  align-items: stretch;
  flex: 1;
  overflow-x: auto;
  scrollbar-width: none;
}
.dash-tabs::-webkit-scrollbar { display: none; }

.dash-tab {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--p-text-color-secondary);
  border-bottom: 2px solid transparent;
  white-space: nowrap;
  transition: color 0.12s, border-color 0.12s, background 0.12s;
  user-select: none;
}
.dash-tab:hover {
  color: var(--p-text-color);
  background: var(--p-surface-100, rgba(0,0,0,.04));
}
.dash-tab.is-active {
  color: var(--p-primary-color, #3b82f6);
  border-bottom-color: var(--p-primary-color, #3b82f6);
}

.tab-name {
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Inline rename input ────────────────────────────────────────────────── */
.tab-rename-input {
  border: 1px solid var(--p-primary-color, #3b82f6);
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 0.85rem;
  font-weight: 500;
  background: var(--p-surface-0, #fff);
  color: var(--p-text-color);
  outline: none;
  width: 140px;
}

/* ── Tab action buttons ─────────────────────────────────────────────────── */
.tab-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}

.tab-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 4px;
  background: none;
  cursor: pointer;
  font-size: 0.65rem;
  color: var(--p-text-color-secondary);
  opacity: 0.7;
  transition: opacity 0.12s, background 0.12s;
}
.tab-btn:hover {
  opacity: 1;
  background: var(--p-surface-200, rgba(0,0,0,.08));
}
.tab-btn--delete:hover {
  background: var(--p-red-100, #fee2e2);
  color: var(--p-red-600, #dc2626);
}

/* ── New dashboard button ──────────────────────────────────────────────── */
.dash-tab-new {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: var(--p-text-color-secondary);
  opacity: 0.6;
  transition: opacity 0.12s, background 0.12s;
  flex-shrink: 0;
}
.dash-tab-new:hover {
  opacity: 1;
  background: var(--p-surface-100, rgba(0,0,0,.04));
}

/* ── Right actions ──────────────────────────────────────────────────────── */
.dash-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  flex-shrink: 0;
  border-left: 1px solid var(--p-content-border-color, #e2e8f0);
}

.dash-saving {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: .72rem;
  color: var(--p-text-color-secondary);
  white-space: nowrap;
}

/* ── Loading ────────────────────────────────────────────────────────────── */
.dash-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  color: var(--p-text-color-secondary);
}

/* ── Empty state ────────────────────────────────────────────────────────── */
.dash-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 0;
  color: var(--p-text-color-secondary);
  font-size: 0.9rem;
}
.dash-empty i { font-size: 2rem; opacity: 0.4; }
.dash-empty p { margin: 0; }

/* ── Sections ───────────────────────────────────────────────────────────── */
.dash-sections {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 12px 0;
  flex: 1;
}

/* ── Dark mode ──────────────────────────────────────────────────────────── */
:global(.my-app-dark) .dash-header {
  background: var(--p-surface-900, #1e293b);
  border-bottom-color: var(--p-surface-700, #334155);
}
:global(.my-app-dark) .dash-actions {
  border-left-color: var(--p-surface-700, #334155);
}
:global(.my-app-dark) .tab-btn--delete:hover {
  background: rgba(220, 38, 38, 0.15);
}
</style>
